import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { ActivatedRoute, Router } from '@angular/router'
import { EventType } from '@app/core/model/enums/event-type.enum'
import { AuthActions } from '@app/core/store'
import { ScenarioService } from '@app/features/company-management/service/scenario.service'
import { CompanyManagementActions } from '@app/features/company-management/store/actions/company-management.actions'
import { CompanyManagementSelectors } from '@app/features/company-management/store/selectors/company-management.selectors'
import { Scenario } from '@app/features/scope-overview/model/scenario.model'
import { MappedEntity } from '@app/features/scoping/models/mapped-entity.model'
import { DataMappingService } from '@app/features/scoping/service/data-mapping.service'
import { ScopeUiTableSelectMode } from '@app/shared/components/ui-components/scope-ui-table/scope-ui-table.component'
import { RegisteredSubscription, WebsocketService } from '@app/shared/services/websocket.service'
import { searchInText } from '@app/shared/utils/search-utils.const'
import { MenuOptions } from '@core/model/definitions/menu-options.interface'
import { GenericModalInput, ModalConfig } from '@core/model/modal-config.model'
import { Preference } from '@core/model/user-preferences.interface'
import { User } from '@core/model/user.model'
import { AuthService } from '@core/service/auth.service'
import { Store } from '@ngrx/store'
import { ScopeUiModalComponent } from '@shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { TableColumnKey } from '@shared/components/ui-components/scope-ui-table/table-column-key.enum'
import { SNACKBAR_LENGTH_SHORT, SnackbarEventType, SnackbarService } from '@shared/utils/snackbar.service'
import { combineLatest, map, Observable } from 'rxjs'
import { UploadType } from '@app/shared/components/upload-modal/models/upload-modal-config'
import { UploadModalComponent } from '@app/shared/components/upload-modal/upload-modal.component'

@Component({
  selector: 'scenarios',
  templateUrl: './scenarios.component.html',
  styleUrls: ['./scenarios.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScenariosComponent implements OnInit {
  loggedInUser!: User
  scenarios$: Observable<Scenario[]>
  loading$: Observable<boolean>
  mappedScenarioDataSource$!: Observable<MappedEntity<Scenario>[]>
  scenarios: Scenario[]
  searchText = ''
  scenarioColumns!: Record<string, Preference>
  scenarioPreferenceColumns!: Preference[]
  scenarioMenuOptions: MenuOptions[]
  scenariosMenuOptions: MenuOptions[]
  registeredSub: RegisteredSubscription

  constructor(
    private store: Store,
    private authService: AuthService,
    private mappingService: DataMappingService,
    private scenarioService: ScenarioService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private webSocketService: WebsocketService
  ) {
    this.loggedInUser = this.authService.loggedInUser
    this.scenarios$ = this.store.select(CompanyManagementSelectors.selectScenarios)
    this.loading$ = this.store.select(CompanyManagementSelectors.selectLoadingScenarios)
  }

  ngOnInit(): void {
    this.registerNotificationListener()
    this.getScenarios()
    this.mapScenarioConfigToDataSource()
    this.initScenarioColumns()
    this.setScenariosMenuOptions()
    this.setScenarioMenuOptions()
  }

  getScenarios() {
    this.store.dispatch(CompanyManagementActions.getScenarios())
  }

  mapScenarioConfigToDataSource() {
    this.mappedScenarioDataSource$ = combineLatest([this.scenarios$]).pipe(
      map(([scenarios]) => {
        return this.mappingService.transformArray(scenarios, this.scenarioPreferenceColumns)
      })
    )
  }

  initScenarioColumns() {
    this.scenarioColumns = {
      NAME: {
        key: TableColumnKey.NAME,
        name: 'Name',
        valueIconFunction: () => {
          return 'edit_document'
        },
        selected: true,
        field: 'name',
      },
      CREATED_BY: {
        key: TableColumnKey.CREATED_BY,
        name: 'Created By',
        selected: true,
        value: (scenario: Scenario) => {
          return scenario.auditMetadata?.createdBy?.getFullname()
        },
      },
      CREATED_DATE: {
        key: TableColumnKey.DATE_CREATED,
        name: 'Created Date',
        selected: true,
        field: 'auditMetadata.createdOn',
      },
      PUBLISHED: {
        key: TableColumnKey.PUBLISHED,
        name: 'Published',
        selected: true,
        value: (scenario: Scenario) => (scenario.state === TableColumnKey.PUBLISHED ? 'Yes' : 'No'),
      },
    }
    this.initScenarioPreferenceColumns()
  }

  initScenarioPreferenceColumns() {
    this.scenarioPreferenceColumns = Object.values(this.scenarioColumns)
  }

  filterScenarios() {
    const filteredScenarios$ = this.scenarios$.pipe(
      map((scenarios) => scenarios.filter((scenario) => searchInText(scenario.name, this.searchText)))
    )
    this.mappedScenarioDataSource$ = filteredScenarios$.pipe(
      map((filteredScenarios) => this.mappingService.transformArray(filteredScenarios, this.scenarioPreferenceColumns))
    )
  }

  openCreateModal() {
    const inputs: GenericModalInput[] = [
      { name: 'name', control: new FormControl(''), type: 'text', required: true, label: `Form name`, maxLength: 50 },
    ]

    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        'Add Scenario Form',
        `Create a new Scenario Form`,
        `Add Scenario Form`,
        undefined,
        (form: FormControl) => {
          const scenario = { name: form.get('name').value }
          this.scenarioService.create(scenario).subscribe({
            next: (result) => {
              dialog.close()
              this.store.dispatch(CompanyManagementActions.createScenarioSuccess({ scenario: result }))
              this.snackbarService.showSnackbar(
                `Scenario Form ${result.name} was successfully created.`,
                SNACKBAR_LENGTH_SHORT,
                SnackbarEventType.SUCCESS
              )
            },
            error: () => {
              this.snackbarService.showDefaultErrorSnackbar()
            },
          })
        },
        undefined,
        inputs,
        false,
        true,
        undefined,
        (form: FormControl) => !!form.get('name')?.value
      ),
    })
  }

  onSelect(scenario: Scenario) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: 'scenarios', id: scenario.id },
      replaceUrl: true,
    })
  }

  onDelete(scenario: Scenario) {
    const scenarioDeleteModalConfig: ModalConfig = {
      title: `Delete Scenario Form`,
      body: `Are you sure you want to delete ${scenario.name}?`,
      confirmText: 'Delete',
      confirmCallback: () => {
        this.store.dispatch(CompanyManagementActions.deleteScenario({ id: scenario.id }))
        dialog.close()
      },
      cancelCallback: undefined,
      inputs: [],
      isMemberModal: undefined,
      limitBodyWidth: true,
    }
    const dialog = this.dialog.open(ScopeUiModalComponent, { data: scenarioDeleteModalConfig })
  }

  onExport(scenario: Scenario) {
    const scenarioDeleteModalConfig: ModalConfig = {
      title: `Export Scenario Form`,
      body: `Are you sure you want to export ${scenario.name}?`,
      confirmText: 'Export',
      confirmCallback: () => {
        this.store.dispatch(
          CompanyManagementActions.exportScenario({
            id: scenario.id,
            scenarioName: scenario.name,
          })
        )
        dialog.close()
      },
      cancelCallback: undefined,
      inputs: [],
      isMemberModal: undefined,
      limitBodyWidth: true,
    }
    const dialog = this.dialog.open(ScopeUiModalComponent, { data: scenarioDeleteModalConfig })
  }

  uploadScenario() {
    const dialog = this.dialog.open(UploadModalComponent, {
      data: {
        title: `Upload Scenario`,
        titleBody: `Upload Scenario with Excel file`,
        body: `Upload Excel file`,
        type: UploadType.SCENARIO,
        submitFn: (file: File) => this.scenarioService.import(file),
        successFn: () => dialog.close(),
        showBody: true,
      },
    })
  }

  onScenarioEventReceived(event: any) {
    switch (event.object.eventType) {
      case EventType.FILE_IMPORT_SUCCESS:
        this.onScenarioImportSuccess()
        break
      case EventType.FILE_IMPORT_FAILED:
        this.onScenarioImportFailed(event.object.properties.errorMessage)
        break
    }
  }

  onScenarioImportSuccess() {
    this.snackbarService.showSuccessSnackbar(`Scenario was successfully uploaded.`)
    this.getScenarios()
  }

  onScenarioImportFailed(errorMessage: string) {
    this.snackbarService.showErrorSnackbar(errorMessage)
    this.store.dispatch(AuthActions.getUserNotifications())
  }

  private registerNotificationListener() {
    if (!this.registeredSub)
      this.registeredSub = this.webSocketService.registerUserEventReceiver((event: any) =>
        this.onScenarioEventReceived.call(this, event)
      )
  }

  private setScenariosMenuOptions() {
    this.scenariosMenuOptions = [
      {
        icon: () => 'view_agenda',
        name: () => `Upload Scenario`,
        callback: () => this.uploadScenario(),
      },
    ]
  }

  private setScenarioMenuOptions() {
    this.scenarioMenuOptions = [
      {
        callback: (element) => this.onDuplicate(element.entity),
        icon: () => 'content_copy',
        name: () => 'Duplicate',
      },
      { callback: (element) => this.onDelete(element.entity), icon: () => 'delete', name: () => 'Delete' },
      {
        callback: (element) => this.onPublish(element.entity, false),
        icon: () => 'visibility_off',
        name: () => 'Unpublish',
        isHidden: ({ entity }) => entity.state !== TableColumnKey.PUBLISHED,
      },
      {
        callback: (element) => this.onPublish(element.entity, true),
        icon: () => 'visibility',
        name: () => 'Publish',
        isHidden: ({ entity }) => entity.state === TableColumnKey.PUBLISHED,
      },
      {
        callback: (element) => this.onExport(element.entity),
        icon: () => 'visibility',
        name: () => 'Export',
      },
    ]
  }

  onDuplicate(scenario: Scenario) {
    this.store.dispatch(CompanyManagementActions.duplicateScenario({ id: scenario.id, scenarioName: scenario.name }))
  }

  private onPublish(scenario: Scenario, published: boolean) {
    const updated: Scenario = { ...scenario, state: published ? TableColumnKey.PUBLISHED : TableColumnKey.UNPUBLISHED }
    this.store.dispatch(CompanyManagementActions.updateScenario({ id: scenario.id, scenario: updated }))
  }

  protected readonly ScopeUiTableSelectMode = ScopeUiTableSelectMode
}
